import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { CircularProgress, Box } from '@mui/material';
import {
  initializeEnvironmentId,
  redirectToAuthBridge,
  getEnvironmentId,
  switchEnvironment
} from '../utils/environmentId';

type EnvironmentContextType = {
  environmentId: string;
  switchEnvironment: () => void;
};

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined
);

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error(
      'useEnvironment must be used within an EnvironmentProvider'
    );
  }
  return context;
};

type Props = {
  children: ReactNode;
};

export const EnvironmentProvider = ({ children }: Props) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const hasEnvironment = initializeEnvironmentId();

    if (!hasEnvironment) {
      redirectToAuthBridge();
      return;
    }

    setIsReady(true);
  }, []);

  if (!isReady) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <EnvironmentContext.Provider
      value={{
        environmentId: getEnvironmentId()!,
        switchEnvironment
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};
